import {
  ApostleFilterInput,
  ApostleLite,
  ApostleNormalSortItems,
  ApostleSortBy
} from '@apeiron/apostle'
import { SortInput } from '@apeiron/common'
import ApostleListPagingView from '@src/components/apostles/list/ApostleListPagingView'
import { GAAssetType } from '@src/constants/googleAnalytics'
import useAposleFilterQuery from '@src/hooks/apeiron/apostle/useApostleFilterQuery'
import useTracking from '@src/hooks/common/analytics/useTracking'
import useQueryPaging from '@src/hooks/common/router/useQueryPaging'
import useGetMyApostles, {
  GetMyApostlesRequestInput
} from '@src/hooks/graphql/useGetMyApostles'
import { ReactNode, useCallback, useEffect, useRef } from 'react'

const FetchedApostleListPersonal = (props: Props) => {
  const {
    className,
    popupMode,
    bottomBar,
    renderCard,
    defaultFilterInput,
    defaultSortInput,
    withTracking = false,
    bookmarked = false,
    title,
    thirdNavBar,
    overrideRequestParameters
  } = props

  const { gtmEmitViewItemList, gtmHandleListFilterChange } = useTracking()

  const requestParamUpdatedRef = useRef(false)

  const {
    loading: myApostlesLoading,
    fetch: fetchMyApostles,
    data: myApostles,
    pageInfo: myApostlesPageInfo
  } = useGetMyApostles()

  const queryParams = { supportUrl: !popupMode }

  const { queryPageNumber, setPageNumberQuery } = useQueryPaging(queryParams)

  const { filterInput, sortInput, setBothInput, setFilterInput, setSortInput } =
    useAposleFilterQuery(queryParams)

  const handleOnFilterOrSortChange = useCallback(
    (filter?: ApostleFilterInput, sort?: SortInput<ApostleSortBy>) => {
      if (filter && sort) {
        setBothInput(filter, sort)
      } else {
        if (filter) setFilterInput(filter)
        if (sort) setSortInput(sort)
      }

      if (withTracking && filter) {
        // GA-vAOzjsB7KKVDHqpI
        gtmHandleListFilterChange(GAAssetType.Apostle, filterInput, filter)
      }

      if (!queryParams.supportUrl) {
        setPageNumberQuery(1)
      }
    },
    [
      withTracking,
      queryParams.supportUrl,
      setBothInput,
      setFilterInput,
      setSortInput,
      gtmHandleListFilterChange,
      filterInput,
      setPageNumberQuery
    ]
  )

  useEffect(() => {
    if (defaultFilterInput || defaultSortInput) {
      handleOnFilterOrSortChange(defaultFilterInput, defaultSortInput)
      requestParamUpdatedRef.current = true
    }
  }, [defaultFilterInput, defaultSortInput])

  useEffect(() => {
    if (requestParamUpdatedRef.current === false) {
      const originalRequestInput = {
        bookmarked,
        pageInput: {
          page: queryPageNumber
        },
        filterInput,
        sortInput
      }

      const requestInput: GetMyApostlesRequestInput = overrideRequestParameters
        ? overrideRequestParameters(originalRequestInput)
        : originalRequestInput

      fetchMyApostles(requestInput).then(res => {
        if (withTracking) {
          // GA-Ks6di3aFmWJAYsI
          gtmEmitViewItemList({
            type: GAAssetType.Apostle,
            items: res.data
          })
        }
      })
    }

    requestParamUpdatedRef.current = false
  }, [
    bookmarked,
    filterInput,
    sortInput,
    queryPageNumber,
    fetchMyApostles,
    gtmEmitViewItemList,
    withTracking,
    overrideRequestParameters
  ])

  return (
    <ApostleListPagingView
      className={`Custom-ApostleListPagingView ${className}`}
      title={title}
      popupMode={popupMode}
      apostles={myApostles}
      onPageNumberChange={setPageNumberQuery}
      filterInput={filterInput}
      sortInput={sortInput}
      onFilterOrSortChange={handleOnFilterOrSortChange}
      loading={myApostlesLoading}
      pageInfo={myApostlesPageInfo}
      sortByDropdownOptions={ApostleNormalSortItems}
      bottomBar={bottomBar}
      thirdNavBar={thirdNavBar}
      renderCard={renderCard}
    />
  )
}

type Props = {
  className?: string
  title?: string
  popupMode?: boolean
  bottomBar?: ReactNode
  defaultFilterInput?: ApostleFilterInput
  defaultSortInput?: SortInput<ApostleSortBy>
  renderCard: (apostle: ApostleLite) => ReactNode
  bookmarked?: boolean
  withTracking?: boolean
  thirdNavBar?: ReactNode
  overrideRequestParameters?: (
    requestInput: GetMyApostlesRequestInput
  ) => GetMyApostlesRequestInput
}

export type { Props as ApostleListPersonalProps }

export default FetchedApostleListPersonal
